import React, { ComponentPropsWithRef, useEffect, useState } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { Button } from '../../components/Button/index';
import { useItemsLoader } from '@/hooks/useItemsLoader';
import { fetchGroups } from '@/features-slice/group/GroupSlice';
import { Icons } from '@/components/icons';
import 'dayjs/locale/fr';
import 'dayjs/locale/en';
import { useTranslation } from '@/i18n/useTranslation';
import { Group } from '@ionnyk-npm/common-ts';

const freqList = [
  {
    value: 30,
    label: '30s',
  },
  {
    value: 60,
    label: '1min',
  },
  {
    value: 120,
    label: '2min',
  },
  {
    value: 300,
    label: '5min',
  },
  {
    value: 600,
    label: '10min',
  },
  {
    value: 900,
    label: '15min',
  },
  {
    value: 1800,
    label: '30min',
  },
  {
    value: 3600,
    label: '1h',
  },
  {
    value: 3600 * 24,
    label: '1d',
  },
  {
    value: 3600 * 24 * 7,
    label: '1w',
  },
  {
    value: 3600 * 24 * 7 * 30,
    label: '1mo',
  },
];

type Props = ComponentPropsWithRef<'div'> & {
  rootGroup: Group;
  onClose: () => void;
  batchUuid: string;
  onConfirm: (startDate: Date, groupUuid: string, frequencySeconds: number) => void;
};

export const DispatchModal = (p: Props) => {
  const { rootGroup, onClose, onConfirm, batchUuid, style } = p;
  // TODO date should be in the future and reset @ modal open
  const [startDate, setStartDate] = useState<dayjs.Dayjs>(dayjs().add(1, 'hour'));
  // const [endDate, setEndDate] = useState<dayjs.Dayjs>(null);
  // eslint-disable-next-line prefer-const
  let { entities: groups, loading } = useItemsLoader({
    rootEntitiesSelector: (s) => s.group,
    loaderThunk: fetchGroups,
  });

  useEffect(() => {
    if (batchUuid) {
      // open modal -> reset date
      setStartDate(dayjs().add(15, 'minute'));
    }
  }, [batchUuid]);

  // TODO bug backend
  console.debug('groups', groups);
  groups = groups.length ? groups : [rootGroup];

  const [selection, setSelection] = useState<{
    batchUuid: string;
    groupUuid: string;
    frequencySeconds: number;
  } | null>({
    batchUuid,
    groupUuid: rootGroup.uuid,
    frequencySeconds: 900, // DEFAULT 15m
  });

  const t = useTranslation();

  return (
    <Dialog
      open={!!batchUuid}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <DialogTitle sx={{ mr: 2, p: 2 }} id="customized-dialog-title">
        {t('batch.modal.title')}
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={() => onClose()}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers className="p-8 space-y-4">
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="fr">
          <Box sx={style}>
            {/* TODO optionnal = NOW */}
            <DateTimePicker
              disablePast
              label={t('batch.startdate')}
              value={startDate}
              onChange={(date) => setStartDate(date)}
            />
            {/* <DateTimePicker
              label={t('batch.enddate')}
              minDate={startDate.add(1, 'day')}
              value={endDate}
              onChange={(date) => setEndDate(date)}
            /> */}
          </Box>
        </LocalizationProvider>
        {/*  */}
        <Box sx={style}>
          <FormControl fullWidth>
            <InputLabel>Frequency</InputLabel>
            <Select
              value={String(selection?.frequencySeconds)}
              label="Frequency [s]"
              onChange={(event: SelectChangeEvent) => {
                setSelection((s) => ({
                  ...s,
                  frequencySeconds: Number.parseInt(event.target.value),
                }));
              }}
            >
              {freqList.map((freq) => (
                <MenuItem key={freq.value} value={freq.value}>
                  {freq.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        {/*  */}
        <Box sx={style}>
          <FormControl fullWidth>
            <InputLabel>Group - {loading ? 'Loading ...' : groups.length}</InputLabel>
            <Select
              disabled={loading}
              value={String(selection?.groupUuid)}
              label="Group"
              onChange={(event: SelectChangeEvent) => {
                setSelection((s) => ({
                  ...s,
                  groupUuid: event.target.value,
                }));
              }}
            >
              {groups.map((g) => (
                <MenuItem key={g.uuid} value={g.uuid}>
                  {g.name }
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          className="w-3/4 mx-auto"
          autoFocus
          onClick={() =>
            onConfirm(startDate.toDate(), selection.groupUuid, selection?.frequencySeconds)
          }
        >
          <Icons.Send fontSize="small" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};
